import React, { useState, useRef } from "react";
import { Container, Row, Col } from "react-bootstrap";
import parse from "html-react-parser";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import "swiper/css";
import "./index.css";

const PlayButton = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="62" height="62" viewBox="0 0 69 69" fill="none">
    <circle cx="34.2143" cy="34.2143" r="34.2143" fill="#FF541C"/>
    <path d="M47.5454 31.616C49.5454 32.7707 49.5454 35.6574 47.5454 36.8121L29.8006 47.0571C27.8006 48.2118 25.3006 46.7684 25.3006 44.459L25.3006 23.9691C25.3006 21.6597 27.8006 20.2163 29.8006 21.371L47.5454 31.616Z" fill="white"/>
  </svg>
);

const CheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
    <g clipPath="url(#clip0_150_3364)">
      <path d="M6.21533 7.59684L8.28711 9.66862L15.193 2.7627" stroke="#E8A518" strokeWidth="1.38118" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M14.5024 8.28662V13.1208C14.5024 13.4871 14.3569 13.8384 14.0979 14.0974C13.8389 14.3564 13.4876 14.502 13.1213 14.502H3.45296C3.08665 14.502 2.73534 14.3564 2.47632 14.0974C2.21729 13.8384 2.07178 13.4871 2.07178 13.1208V3.45247C2.07178 3.08616 2.21729 2.73485 2.47632 2.47583C2.73534 2.21681 3.08665 2.07129 3.45296 2.07129H11.0495" stroke="#E8A518" strokeWidth="1.38118" strokeLinecap="round" strokeLinejoin="round"/>
    </g>
    <defs>
      <clipPath id="clip0_150_3364">
        <rect width="16.5742" height="16.5742" fill="white"/>
      </clipPath>
    </defs>
  </svg>
);

const getVideoId = (url) => {
  if (!url) return "";
  const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|live\/|shorts\/))([-\w]+)/);
  return match ? match[1] : "";
};

const VerticalCarousel = ({ images, startReverse }) => {
  const swiperRef = useRef(null);
  const hoveredRef = useRef(false);

  return (
    <div
      className="announce-vertical-carousel"
      onMouseEnter={() => { hoveredRef.current = true; swiperRef.current?.autoplay?.stop(); }}
      onMouseLeave={() => { hoveredRef.current = false; swiperRef.current?.autoplay?.start(); }}
    >
      <div className="announce-vertical-gradient" />
      <Swiper
        modules={[Autoplay]}
        onSwiper={(s) => (swiperRef.current = s)}
        direction="vertical"
        slidesPerView={6}
        spaceBetween={12}
        loop={true}
        speed={2000}
        autoplay={{
          delay: 0,
          disableOnInteraction: false,
          reverseDirection: startReverse,
        }}
        allowTouchMove={false}
        className="announce-vertical-swiper"
      >
        {images.map((img, i) => (
          <SwiperSlide key={i} className="announce-vertical-slide">
            <img src={`${process.env.REACT_APP_IMAGES}${img}`} alt="" className="announce-carousel-img" loading="lazy" />
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
};

const AnnouncementSection = ({ data }) => {
  const [playing, setPlaying] = useState(false);
  const imgPath = process.env.REACT_APP_IMAGES;

  if (!data) return null;

  const videoId = getVideoId(data.videoUrl);
  const images = data.carouselImages || [];

  return (
    <section className="announce-section section-spacing">
      {/* <span className="video-glow-blob-1" style={{top:'7%',left:'30%'}} />
      <span className="video-glow-blob-2" style={{top:'7%',left:'40%'}} />
      <span className="video-glow-blob-1" style={{top:'7%',left:'50%',background:'#FF541C'}} />
      <span className="video-glow-blob-2" style={{top:'7%',right:'30%',background:'#F31454'}} /> */}
      <Container>
        <div className="announce-inner">
          <Row>
            <Col lg={3} className="announce-side-col announce-side-left d-none d-lg-block">
              <VerticalCarousel images={images} startReverse={false} />
            </Col>

            <Col lg={6} className="announce-center-col">
              <div className="announce-badge-row">
                <span className="announce-badge-square" />
                <span className="announce-badge">{parse(data.badge)}</span>
              </div>
              <h2 className="announce-heading">{parse(data.heading)}</h2>

              <div className="announce-video-wrapper">
                <div className="announce-thumb-click" onClick={() => !playing && setPlaying(true)} role="button" tabIndex={0} aria-label="Play video">
                    <img src={`${imgPath}${data.thumbnail}`} alt={data.badge} className="announce-thumbnail" loading="lazy" />
                    {!playing && <span className="announce-play-btn"><PlayButton /></span>}
                    {playing && (
                      <>
                        <div className="video-loading-spinner" />
                        <iframe
                          className="announce-iframe video-iframe-overlay"
                          src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
                          title={data.badge}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          frameBorder="0"
                        />
                        <button className="announce-close-btn" onClick={(e) => { e.stopPropagation(); setPlaying(false); }} aria-label="Close video">✕</button>
                      </>
                    )}
                  </div>
              </div>

              {data.quote && <p className="announce-quote">{parse(data.quote)}</p>}
              {data.description && <p className="announce-description">{parse(data.description)}</p>}

              {data.checklist && (
                <ul className="announce-checklist">
                  {data.checklist.map((item, i) => (
                    <li key={i}><CheckIcon /> <span>{parse(item)}</span></li>
                  ))}
                </ul>
              )}

              {data.footerText && <p className="announce-footer-text">{parse(data.footerText)}</p>}
            </Col>

            <Col lg={3} className="announce-side-col announce-side-right d-none d-lg-block">
              <VerticalCarousel images={images} startReverse={true} />
            </Col>
          </Row>

          {/* Mobile horizontal carousel */}
          <div className="announce-mobile-carousel d-lg-none">
            <Swiper
              modules={[Autoplay]}
              slidesPerView={1.5}
              spaceBetween={10}
              loop={true}
              speed={3000}
              autoplay={{ delay: 0, disableOnInteraction: false }}
              allowTouchMove={true}
            >
              {images.map((img, i) => (
                <SwiperSlide key={i}>
                  <img src={`${imgPath}${img}`} alt="" className="announce-carousel-img" loading="lazy" />
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </div>
      </Container>
    </section>
  );
};

export default AnnouncementSection;
